import { Box, Typography } from "@mui/material";
import {
  IBandwidthFailuresResponseModel,
  IStatisticsUptimeResponseModel,
  SystemPerformanceResponseModel,
} from "../../system-performance/models/chart";

import ChartModalContainer from "../components/ChartModalContainer";
import { GridColDef } from "@mui/x-data-grid";
import React from "react";
import { eventType } from "../../../../../API";
import { extractNodeName } from "../common/helpers";

export const useSystemHealthTableColumns = (
  data: SystemPerformanceResponseModel[],
  dialogTitle: string,
  tableItemName: string,
  eventTypeActual?: eventType
) => {
  const uniqueDates: string[] = React.useMemo(() => {
    const now = new Date();

    // For bandwidth failures, generate last 7 days including today
    if (eventTypeActual === eventType.bandwidthFailures) {
      const dates: string[] = [];
      const setOfDatesFromBEQuery = new Set<string>();

      data.map(datum => {
        if (datum.event_type === eventType.bandwidthFailures) {
          const dayStart = (datum as IBandwidthFailuresResponseModel).day_start;
          const formattedDate =
            typeof dayStart === "string"
              ? dayStart.slice(0, 10)
              : dayStart.toISOString().slice(0, 10);

          setOfDatesFromBEQuery.add(formattedDate);
        }
      });

      // Generate 7 days starting from 6 days ago up to today
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);

        date.setDate(date.getDate() - i);

        dates.push(date.toISOString().slice(0, 10));
      }

      return Array.from(setOfDatesFromBEQuery) as string[];
    }

    const setOfDatesFromBEQuery = new Set<string>();

    data.map(datum => {
      if (datum.event_type !== eventType.bandwidthFailures) {
        const dayStart = (datum as IStatisticsUptimeResponseModel).week_start;
        const formattedDate =
          typeof dayStart === "string"
            ? dayStart.slice(0, 10)
            : dayStart.toISOString().slice(0, 10);

        setOfDatesFromBEQuery.add(formattedDate);
      }
    });

    const currentWeekStart = new Date(now);
    const dayOfWeek = now.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    currentWeekStart.setDate(now.getDate() - daysToMonday);

    // Start 8 weeks before the current week
    const startDate = new Date(currentWeekStart);

    startDate.setDate(startDate.getDate() - 8 * 7);

    const dates: string[] = [];

    // Generate 8 weeks of dates (beginning of each week), excluding current week
    for (let i = 0; i < 8; i++) {
      const date = new Date(startDate);

      date.setDate(date.getDate() + i * 7);

      dates.push(date.toISOString().slice(0, 10)); // Format as YYYY-MM-DD
    }

    return Array.from(setOfDatesFromBEQuery);
  }, [eventTypeActual]);

  const dateColumns: GridColDef[] = React.useMemo(() => {
    return uniqueDates.map(dateStr => ({
      field: `uptimePct-${dateStr}`,
      headerName: dateStr,
      headerAlign: "center",
      disableColumnMenu: true,

      flex: 1,
      minWidth: 100,
      sortComparator: (v1: string, v2: string): number => {
        const toNumber = (val: string): number => {
          if (!val || val.trim() === "-") return Number.NEGATIVE_INFINITY;

          const num = parseFloat(val.replace("%", "").trim());

          return isNaN(num) ? Number.NEGATIVE_INFINITY : num;
        };

        return toNumber(v1) - toNumber(v2);
      },
      hideable: false,
      renderCell: params => {
        let value = "-";
        const noValueSign = "-";

        value = params.row[`uptimePct-${dateStr}`];

        if (value === undefined || value === null || value === "") {
          value = noValueSign;
        }

        return (
          <Typography
            sx={{
              width: "100%",
              color: theme =>
                value === noValueSign
                  ? theme.palette.otherTextTertiary.main
                  : "inherit",
              textAlign: "center",
            }}
            variant="body2Regular"
          >
            {value}
          </Typography>
        );
      },
    }));
  }, [uniqueDates, data]);

  const columns: GridColDef[] = React.useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "itemName",
        renderHeader: () => {
          return (
            <Box sx={{ pl: "2em", fontWeight: "bold" }}>{tableItemName}</Box>
          );
        },
        flex: 1,
        minWidth: 150,
        hideable: false,
        cellClassName: "stickyCell",
        headerClassName: "stickyHeader",
        disableColumnMenu: true,
        sortComparator: (v1: string, v2: string) =>
          extractNodeName(v1).localeCompare(extractNodeName(v2), undefined, {
            sensitivity: "base",
          }),
        renderCell: params => (
          <ChartModalContainer
            params={params}
            data={data}
            dialogTitle={dialogTitle}
          />
        ),
      },
      ...dateColumns,
    ];

    return cols;
  }, [dateColumns]);

  return { columns };
};
